// src/lib/observability — the ONLY door to Sentry + PostHog from app code.
//
// Ports-and-adapters in miniature: app code talks to this façade
// (captureException / identifyUser / clearUser / trackEvent); the vendor SDKs
// live behind `./sentry` and `./posthog` and are loaded with dynamic `import()`
// ONLY when their build-time key is present (`VITE_SENTRY_DSN` /
// `VITE_POSTHOG_KEY` — see README §Environment). With neither key set this
// module is a zero-network no-op and the SDK chunks are never fetched, so the
// initial bundle stays lean (Lighthouse ≥0.95 invariant, §5.6).
//
// Privacy contract (audit 2026-08-18 §1/§2 — keep it this way):
//   - user identity is the Supabase `auth.users.id` ONLY — never email/phone
//   - Sentry `sendDefaultPii: false`; no Session Replay integration
//   - PostHog autocapture OFF, session recording OFF, surveys OFF,
//     `person_profiles: 'identified_only'`, no remote <script> injection
//
// Early calls (before the lazy SDK chunk resolves, or in a build without keys)
// are buffered (exceptions, last-known user) and flushed on init, so a crash
// during boot is not lost.

import { env } from '@/lib/env';

// Option bags below spell out `| undefined` because tsconfig.base has
// `exactOptionalPropertyTypes` — callers routinely pass `info.componentStack`
// (string | null | undefined) and env values (string | undefined) straight in.
export interface CaptureContext {
  /** React component stack from an ErrorBoundary / router catch boundary. */
  componentStack?: string | null | undefined;
  /** Low-cardinality tags (e.g. `{ boundary: 'lane', lane: 'today' }`). */
  tags?: Record<string, string> | undefined;
}

export interface ErrorSink {
  captureException(error: unknown, ctx?: CaptureContext): void;
  setUser(id: string | null): void;
}

export interface AnalyticsSink {
  identify(id: string): void;
  reset(): void;
  pageview(): void;
  capture(event: string, props?: Record<string, unknown>): void;
}

/**
 * Minimal structural view of the TanStack Router instance — just enough to
 * hook SPA navigations for pageviews + Sentry route-change transactions.
 * Typed structurally so tests can pass a stub and the façade never imports
 * the (heavy) router module itself.
 */
export interface ObservableRouter {
  subscribe(
    eventType: 'onResolved',
    fn: (event: { pathChanged: boolean; toLocation: { pathname: string } }) => void,
  ): () => void;
}

export interface ObservabilityConfig {
  sentryDsn?: string | undefined;
  sentryEnv?: string | undefined;
  posthogKey?: string | undefined;
  /** Sentry `release` / PostHog super-property. `studio-ui@<sha>` when known. */
  release?: string | undefined;
}

export interface InitOptions {
  router: ObservableRouter;
  /** Test seam — defaults to the validated build-time env. */
  config?: ObservabilityConfig | undefined;
}

const MAX_BUFFERED_ERRORS = 20;

let errorSink: ErrorSink | null = null;
let analyticsSink: AnalyticsSink | null = null;
let started: Promise<void> | null = null;
let currentUserId: string | null = null;
let pendingErrors: Array<{ error: unknown; ctx: CaptureContext | undefined }> = [];

function configFromEnv(): ObservabilityConfig {
  return {
    sentryDsn: env.SENTRY_DSN,
    sentryEnv: env.SENTRY_ENV,
    posthogKey: env.POSTHOG_KEY,
    release: env.GIT_SHA ? `studio-ui@${env.GIT_SHA}` : undefined,
  };
}

/**
 * Boot the configured sinks. Idempotent — repeated calls return the same
 * promise and never re-initialise an SDK. Resolves once every enabled SDK has
 * been imported + initialised (or immediately when nothing is configured).
 */
export function initObservability(opts: InitOptions): Promise<void> {
  if (started) return started;
  const config = opts.config ?? configFromEnv();
  const { router } = opts;

  started = (async () => {
    const tasks: Array<Promise<void>> = [];

    const { sentryDsn, posthogKey, sentryEnv: environment, release } = config;

    if (sentryDsn) {
      tasks.push(
        import('./sentry').then(({ createSentrySink }) => {
          errorSink = createSentrySink({ dsn: sentryDsn, environment, release, router });
        }),
      );
    }

    if (posthogKey) {
      tasks.push(
        import('./posthog').then(({ createPostHogSink }) => {
          analyticsSink = createPostHogSink({ key: posthogKey, environment, release });
        }),
      );
    }

    if (tasks.length === 0) return;
    await Promise.all(tasks);

    // SPA pageviews: fire on every resolved navigation whose PATH changed
    // (search-param churn — filters, sub-tabs — is deliberately NOT a pageview
    // in a dense operator UI). The initial load also resolves through here.
    if (analyticsSink) {
      const sink = analyticsSink;
      router.subscribe('onResolved', (event) => {
        if (event.pathChanged) sink.pageview();
      });
    }

    // Flush anything that happened while the SDK chunk was in flight.
    if (currentUserId !== null) applyUser(currentUserId);
    const backlog = pendingErrors;
    pendingErrors = [];
    for (const { error, ctx } of backlog) errorSink?.captureException(error, ctx);
  })();

  return started;
}

function applyUser(id: string | null): void {
  errorSink?.setUser(id);
  if (!analyticsSink) return;
  if (id === null) analyticsSink.reset();
  else analyticsSink.identify(id);
}

/** Report an exception to the error sink (buffered until init completes). */
export function captureException(error: unknown, ctx?: CaptureContext): void {
  if (errorSink) {
    errorSink.captureException(error, ctx);
    return;
  }
  if (pendingErrors.length >= MAX_BUFFERED_ERRORS) pendingErrors.shift();
  pendingErrors.push({ error, ctx });
}

/** Identify the signed-in operator by Supabase user id — NEVER email. */
export function identifyUser(userId: string): void {
  if (currentUserId === userId) return;
  currentUserId = userId;
  applyUser(userId);
}

/** Clear identity on sign-out (Sentry user → null, PostHog → reset()). */
export function clearUser(): void {
  if (currentUserId === null) return;
  currentUserId = null;
  applyUser(null);
}

/**
 * Capture a high-value operator event. Intentionally unused by lanes today —
 * the dashboard has no analytics hooks yet and we do not sprinkle events.
 */
export function trackEvent(event: string, props?: Record<string, unknown>): void {
  analyticsSink?.capture(event, props);
}

/** Test-only: wipe module state between cases. */
export function __resetObservabilityForTests(): void {
  errorSink = null;
  analyticsSink = null;
  started = null;
  currentUserId = null;
  pendingErrors = [];
}
